import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, register, confirmRegistration } from '../services/auth';
import { signInWithRedirect } from 'aws-amplify/auth';

type Mode = 'login' | 'signup' | 'confirm';

const AGENTS = [
  {
    name: 'Executive Polish Agent',
    description: 'Turns rough drafts into polished, executive-ready communication',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
      </svg>
    ),
  },
  {
    name: 'PM Coach Agent',
    description: 'Your AI PM guide, grounded in PMBOK and PM best practices',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="5" />
        <path d="M20 21a8 8 0 0 0-16 0" />
      </svg>
    ),
  },
];

export default function Login() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const isLocal = import.meta.env.VITE_COGNITO_USER_POOL_ID?.startsWith('local');

  if (isLocal) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--bg-primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: '16px',
          padding: '40px',
          textAlign: 'center',
        }}>
          <h2 style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>
            Local Development Mode
          </h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>
            Auth is bypassed locally.
          </p>
          <a href="/editor" style={{ color: 'var(--accent)' }}>Go to Editor →</a>
        </div>
      </div>
    );
  }

  async function handleGoogleSignIn() {
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch {
      setError('Google sign-in failed. Please try again.');
    }
  }

  async function handleSubmit() {
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        navigate('/dashboard');
      } else if (mode === 'signup') {
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        await register(email, password);
        setMode('confirm');
      } else {
        await confirmRegistration(email, code);
        await login(email, password);
        navigate('/dashboard');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Something went wrong';
      setError(msg.replace('UsernameExistsException: ', '')
                  .replace('UserNotConfirmedException: ', '')
                  .replace('NotAuthorizedException: ', ''));
    } finally {
      setLoading(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    borderRadius: '10px',
    border: '1px solid var(--border)',
    background: 'var(--bg-elevated)',
    color: 'var(--text-primary)',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: "'Inter', system-ui, sans-serif",
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily: "'Inter', system-ui, sans-serif",
      position: 'relative',
      overflow: 'hidden',
    }} className="grid-bg">

      {/* Logo */}
      <div style={{ marginBottom: '32px', textAlign: 'center' }}>
        <div style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: '36px',
          fontWeight: 700,
          letterSpacing: '-0.02em',
          marginBottom: '8px',
        }}>
          <span style={{ color: 'var(--accent)' }}>My</span>
          <span style={{ color: 'var(--text-primary)' }}>IQ</span>
        </div>
        <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
          The AI partner for PMs and TPMs.
        </p>
      </div>

      {/* Agent showcase */}
      {mode !== 'confirm' && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '20px',
          width: '100%',
          maxWidth: '760px',
          marginBottom: '28px',
        }}>
          {AGENTS.map(agent => (
            <div
              key={agent.name}
              style={{
                flex: '1 1 260px',
                maxWidth: '360px',
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: '18px',
                padding: '28px 26px',
              }}
            >
              <div style={{ width: '40px', height: '40px', marginBottom: '18px', color: 'var(--accent)' }}>
                {agent.icon}
              </div>
              <h3 style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: '18px',
                fontWeight: 700,
                lineHeight: 1.25,
                color: 'var(--text-primary)',
                margin: '0 0 10px 0',
              }}>
                {agent.name}
              </h3>
              <p style={{ fontSize: '13.5px', color: 'var(--text-muted)', lineHeight: 1.55, margin: 0 }}>
                {agent.description}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Card */}
      <div style={{
        width: '100%',
        maxWidth: '760px',
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
      }}>
        <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
        <h2 style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: '20px',
          fontWeight: 600,
          color: 'var(--text-primary)',
          marginBottom: '24px',
          textAlign: 'center',
        }}>
          {mode === 'login' && 'Welcome back'}
          {mode === 'signup' && 'Create account'}
          {mode === 'confirm' && 'Verify email'}
        </h2>

        {/* Google Sign-In Button */}
        {mode !== 'confirm' && (
          <>
            <button
              onClick={handleGoogleSignIn}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '10px',
                border: '1px solid var(--border)',
                background: 'var(--bg-elevated)',
                color: 'var(--text-primary)',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                transition: 'all 0.2s',
                marginBottom: '20px',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(0,212,255,0.3)';
                e.currentTarget.style.background = 'var(--accent-bg)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.background = 'var(--bg-elevated)';
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            {/* Divider */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
            }}>
              <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>or</span>
              <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
            </div>
          </>
        )}

        {/* Email/password fields */}
        {mode === 'confirm' ? (
          <div>
            <p style={{
              fontSize: '13px', color: 'var(--text-muted)',
              marginBottom: '16px', textAlign: 'center'
            }}>
              We sent a verification code to {email}
            </p>
            <input
              type="text"
              placeholder="Enter verification code"
              value={code}
              onChange={e => setCode(e.target.value)}
              style={inputStyle}
            />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={inputStyle}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={inputStyle}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            />
            {mode === 'signup' && (
              <input
                type="password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                style={inputStyle}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              />
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            marginTop: '12px',
            padding: '10px 14px',
            background: 'var(--error-bg)',
            border: '1px solid var(--error-border)',
            borderRadius: '8px',
            fontSize: '13px',
            color: 'var(--error-text)',
          }}>
            {error}
          </div>
        )}

        {/* Submit button */}
        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{
            width: '100%',
            marginTop: '16px',
            padding: '12px',
            borderRadius: '10px',
            border: 'none',
            background: loading ? 'rgba(0,212,255,0.4)' : 'var(--accent)',
            color: 'var(--btn-primary-text)',
            fontSize: '14px',
            fontWeight: 600,
            fontFamily: "'Outfit', sans-serif",
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            boxShadow: loading ? 'none' : '0 4px 15px rgba(0,212,255,0.3)',
          }}
        >
          {loading ? 'Please wait...' :
            mode === 'login' ? 'Sign in' :
            mode === 'signup' ? 'Create account' :
            'Verify email'}
        </button>

        {/* Mode toggle */}
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          {mode === 'login' ? (
            <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
              Don't have an account?{' '}
              <button
                onClick={() => { setMode('signup'); setError(''); }}
                style={{
                  background: 'none', border: 'none',
                  color: 'var(--accent)', cursor: 'pointer',
                  fontSize: '13px', fontWeight: 500,
                }}
              >
                Sign up
              </button>
            </p>
          ) : mode === 'signup' ? (
            <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
              Already have an account?{' '}
              <button
                onClick={() => { setMode('login'); setError(''); }}
                style={{
                  background: 'none', border: 'none',
                  color: 'var(--accent)', cursor: 'pointer',
                  fontSize: '13px', fontWeight: 500,
                }}
              >
                Sign in
              </button>
            </p>
          ) : null}
        </div>
        </div>
      </div>
    </div>
  );
}
